import { useEffect, useState } from 'react'
import { Alert, Badge, Button, FormSelect } from '../ui'
import {
  emptyMfaCredentials,
  getMfaSetupProviderUi,
  MfaProviderFields,
} from './mfaSetupProviders'

const NO_PROVIDERS = []

export default function MfaSetupStep({ step, onBack, onNext, onConfigure, onVerifyExisting, onSkip }) {
  const providers = step?.providers || NO_PROVIDERS
  const [editing, setEditing] = useState(!step?.decision)
  const [providerKey, setProviderKey] = useState(providers[0]?.key || '')
  const [credentials, setCredentials] = useState(() => emptyMfaCredentials(providers[0]?.key))
  const [replacingExisting, setReplacingExisting] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)
  const provider = providers.find((item) => item.key === providerKey) || providers[0]
  const providerUi = getMfaSetupProviderUi(providerKey)
  const skipped = step?.decision?.disposition === 'skipped'
  const selectedProvider = providers.find((item) => item.key === step?.decision?.providerKey)

  useEffect(() => {
    if (!providers.some((item) => item.key === providerKey)) {
      const nextKey = providers[0]?.key || ''
      if (nextKey !== providerKey) {
        setProviderKey(nextKey)
        setCredentials(emptyMfaCredentials(nextKey))
        setReplacingExisting(false)
      }
    }
  }, [providerKey, providers])

  const configure = async () => {
    if (!providerUi?.complete(credentials)) {
      setError('All provider credentials are required')
      return
    }
    setSaving(true)
    setError(null)
    try {
      await onConfigure(providerKey, credentials)
      setCredentials(emptyMfaCredentials(providerKey))
      setEditing(false)
    } catch {
      setError('We couldn\'t verify this provider. Check the details and try again.')
    } finally {
      setSaving(false)
    }
  }

  const verifyExisting = async () => {
    setSaving(true)
    setError(null)
    try {
      await onVerifyExisting(providerKey)
      setEditing(false)
    } catch {
      setError(`We couldn't verify the existing ${providerUi?.label || 'provider'} configuration. Update its credentials and try again.`)
    } finally {
      setSaving(false)
    }
  }

  const skip = async () => {
    setSaving(true)
    setError(null)
    try {
      await onSkip()
      setEditing(false)
    } catch {
      setError('We couldn\'t save your choice. Try again.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-medium text-[var(--app-text)]">Choose an MFA provider</h2>
          <p className="mt-1 text-sm text-[var(--app-text-muted)]">
            Set up an available provider now, or explicitly skip it for later. Skipping does not enable an MFA policy.
          </p>
        </div>
        {step?.decision && <Badge color={skipped ? 'secondary' : 'info'}>{skipped ? 'Skipped' : 'Verified'}</Badge>}
      </div>

      {error && <Alert color="danger">{error}</Alert>}

      {step?.decision && !editing ? (
        <div className="rounded-[var(--app-radius)] border border-[var(--app-border)] bg-[var(--app-surface-2)] p-3">
          <div className="text-xs text-[var(--app-text-muted)]">MFA choice</div>
          <div className="mt-1 font-medium text-[var(--app-text)]">
            {skipped ? 'Skipped for now' : `${selectedProvider?.label || 'MFA provider'} configured and verified`}
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          {provider ? (
            <div className="rounded-[var(--app-radius)] border border-[var(--app-border)] p-3 space-y-3">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <span className="font-medium">{provider.label}</span>
                  {!provider.available && <Badge color="secondary">Not available</Badge>}
                </div>
                <div className="flex gap-3 text-sm">
                  <a className="text-[var(--app-accent)] underline" href={provider.acquisition_url} target="_blank" rel="noreferrer">Create or purchase account</a>
                  <a className="text-[var(--app-accent)] underline" href={provider.documentation_url} target="_blank" rel="noreferrer">Setup guide</a>
                </div>
              </div>
              {provider.available ? (
                <>
                  {providers.length > 1 && (
                    <FormSelect
                      aria-label="MFA provider"
                      value={providerKey}
                      disabled={saving}
                      onChange={(event) => {
                        setProviderKey(event.target.value)
                        setCredentials(emptyMfaCredentials(event.target.value))
                        setReplacingExisting(false)
                      }}
                    >
                      {providers.map((item) => <option key={item.key} value={item.key}>{item.label}</option>)}
                    </FormSelect>
                  )}
                  {provider.configured && !replacingExisting ? (
                    <>
                      <Alert color="info">{provider.label} is already configured for this workspace. Verify the existing configuration to continue.</Alert>
                      <div className="flex flex-wrap gap-2">
                        <Button color="primary" disabled={saving} onClick={verifyExisting}>{saving ? 'Verifying…' : `Verify existing ${providerUi?.label || provider.label}`}</Button>
                        <Button disabled={saving} onClick={() => setReplacingExisting(true)}>Update credentials</Button>
                      </div>
                    </>
                  ) : providerUi ? (
                    <>
                      <MfaProviderFields providerKey={providerKey} credentials={credentials} onChange={setCredentials} disabled={saving} />
                      <div className="flex flex-wrap gap-2">
                        <Button color="primary" disabled={saving} onClick={configure}>{saving ? 'Saving and verifying…' : `Save and verify ${providerUi.label}`}</Button>
                        {provider.configured && <Button disabled={saving} onClick={() => setReplacingExisting(false)}>Use existing configuration</Button>}
                      </div>
                    </>
                  ) : (
                    <Alert color="warning">This provider can\'t be configured here yet. Choose another provider or skip for now.</Alert>
                  )}
                </>
              ) : (
                <p className="text-xs text-[var(--app-text-muted)]">You can create an account now, then enable this provider for the workspace later.</p>
              )}
            </div>
          ) : (
            <Alert color="info">MFA setup isn\'t available right now. You can skip this step and configure it later.</Alert>
          )}
          <Button disabled={saving} onClick={skip}>Skip for now</Button>
        </div>
      )}

      <div className="flex flex-wrap items-center justify-between gap-2">
        <Button onClick={onBack}>Back</Button>
        <div className="flex gap-2">
          {step?.decision && !editing && <Button onClick={() => setEditing(true)}>Edit</Button>}
          {step?.decision && !editing && <Button color="primary" onClick={onNext}>Next</Button>}
        </div>
      </div>
    </div>
  )
}
